import type * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useAppSelector } from '@doda/common/src/redux/hooks';
import { memberInfoSelector } from '@doda/common/src/redux/features/memberInfo';
import { measureClickMoreSkillTag } from '@doda/common/src/logics/jobSearchDetail/analytics';
import { SliderTooltip } from 'components/SliderTooltip';
import { measureTagsOverflow } from '@doda/common/src/logics/jobSearchDetail/measureTagsOverflow';
import styles from './index.module.scss';

/** MatchedTagCassetteProps */
export interface MatchedTagCassetteProps {
  matchedTagList: string[];
}

export const MatchedTagCassette = ({
  matchedTagList,
}: MatchedTagCassetteProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);

  const memberInfo = useAppSelector(memberInfoSelector);
  const userLastName = memberInfo?.lastName || '';

  const handleToggleExpand = (expandState: boolean) => {
    if (expandState) {
      measureClickMoreSkillTag();
    }

    if (tagsContainerRef.current) {
      tagsContainerRef.current.style.maxHeight = expandState
        ? `${tagsContainerRef.current.scrollHeight}px`
        : '';
    }

    setIsExpanded(expandState);
  };

  // Measure overflow on mount and when tag list changes
  // No ResizeObserver needed: PC cassette width is fixed and doesn't change on resize
  useLayoutEffect(() => {
    if (!tagsContainerRef.current || matchedTagList.length === 0) {
      setHasOverflow(false);
      return;
    }

    const result = measureTagsOverflow(tagsContainerRef, measurementRef);
    // Only update state if value changed
    setHasOverflow((prev) =>
      prev === result.hasOverflow ? prev : result.hasOverflow
    );
  }, [matchedTagList]);

  if (matchedTagList?.length === 0) {
    return null;
  }

  return (
    <div
      className={styles['jobSearchDetail-matchedTagCassette']}
      ref={containerRef}
    >
      {/* Header */}
      <div className={styles['jobSearchDetail-matchedTagCassette__header']}>
        <div
          className={styles['jobSearchDetail-matchedTagCassette__headerTitle']}
        >
          <span>{userLastName}さんの活かせる経験・スキル</span>
          <SliderTooltip
            content={
              <div
                className={
                  styles['jobSearchDetail-matchedTagCassette__tooltipContent']
                }
              >
                Web履歴書の情報をもとに作成されたタグの中から、この求人と合致するものを表示しています。※作成されたタグはプロフィールの「タグ設定」から更新できます。
              </div>
            }
            position="top"
            triggerMode="hover"
            className={styles['jobSearchDetail-matchedTagCassette__tooltip']}
          >
            <img
              src="/cmn_web/img/cm/icon/help_blue.svg"
              alt="活かせる経験・スキルについて"
              width="15"
              height="15"
              style={{ cursor: 'pointer' }}
            />
          </SliderTooltip>
        </div>
        <div
          className={styles['jobSearchDetail-matchedTagCassette__headerCount']}
        >
          全{matchedTagList.length}件
        </div>
      </div>

      {/* Tags Container */}
      <div
        ref={tagsContainerRef}
        id="matched-tags-list"
        data-testid="matched-tags-list"
        role="region"
        aria-label="Matched skills and experience tags"
        className={`${styles['jobSearchDetail-matchedTagCassette__tagsContainer']} ${
          isExpanded
            ? styles['jobSearchDetail-matchedTagCassette__tagsExpanded']
            : styles['jobSearchDetail-matchedTagCassette__tagsSingleLine']
        }`}
      >
        {matchedTagList.map((tag, index) => (
          <span
            key={`tag-${index}`}
            className={styles['jobSearchDetail-matchedTagCassette__tagGroup']}
          >
            <span className={styles['jobSearchDetail-matchedTagCassette__tag']}>
              {tag}
            </span>
            {index < matchedTagList.length - 1 && (
              <span
                className={
                  styles['jobSearchDetail-matchedTagCassette__separator']
                }
              >
                /
              </span>
            )}
          </span>
        ))}
      </div>

      {/* Buttons Container - Outside tags to avoid max-height hiding */}
      {hasOverflow && (
        <div
          className={
            styles['jobSearchDetail-matchedTagCassette__buttonsContainer']
          }
        >
          {/* See More Button */}
          <button
            type="button"
            className={classnames({
              [styles['jobSearchDetail-matchedTagCassette__seeMoreButton']]:
                !isExpanded,
              [styles['jobSearchDetail-matchedTagCassette__closeButton']]:
                isExpanded,
            })}
            onClick={() => handleToggleExpand(!isExpanded)}
            aria-expanded={isExpanded}
            aria-controls="matched-tags-list"
          >
            {!isExpanded ? '…もっと見る' : '閉じる'}
          </button>
        </div>
      )}

      {/* Hidden Measurement Container */}
      <div
        ref={measurementRef}
        className={styles['jobSearchDetail-matchedTagCassette__measurement']}
      />
    </div>
  );
};
